import { Link } from "react-router-dom";
import { CalendarDays, FileText, TrendingUp, Library, Archive, ShieldCheck, Search, Percent, ArrowRight, Download, ClipboardList, CalendarPlus, ExternalLink } from "lucide-react";
import { SITE } from "@/lib/site";
import useReveal from "@/lib/useReveal";

const INTERNAL_RESOURCES = [
  { to: "/resources/record-retention-guide", icon: Archive, title: "Record Retention Guide", desc: "How long to keep tax records and other supporting documents." },
  { to: "/resources/tax-appointment-checklist", icon: ClipboardList, title: "Tax Appointment Checklist", desc: "Documents to gather before your tax preparation appointment." },
  { to: "/resources/tax-due-dates", icon: CalendarDays, title: "Tax Due Dates", desc: "Federal filing deadlines for the year, at a glance." },
  { to: "/resources/taxpayer-rights", icon: ShieldCheck, title: "Taxpayer Rights", desc: "The IRS Taxpayer Bill of Rights: what you're entitled to." },
  { to: "/resources/tax-glossary", icon: Library, title: "Tax Glossary", desc: "Plain-language definitions of common tax terms." },
  { to: "/resources/subscribe-to-tax-calendar", icon: CalendarPlus, title: "Subscribe to Tax Calendar", desc: "Add tax deadlines directly to your personal calendar." },
];

const EXTERNAL_RESOURCES = [
  { href: "https://www.irs.gov/forms-instructions", icon: FileText, title: "IRS Forms & Publications", desc: "Browse the full library of official IRS forms, instructions, and publications on irs.gov." },
  { href: "https://www.irs.gov/filing/federal-income-tax-rates-and-brackets", icon: TrendingUp, title: "Federal Tax Rates & Brackets", desc: "Current federal income tax brackets and rates, published by the IRS." },
  { to: "/resources/withholding", icon: Percent, title: "IRS Tax Withholding Estimator", desc: "Estimate the right amount of federal income tax to withhold from your paycheck." },
  { to: "/resources/refund", icon: Search, title: "Where Is My Refund?", desc: "Check the status of your federal income tax refund." },
];

const DOWNLOADS = [
  { title: "Accounting & Tax Services Checklist", desc: "Outlines the accounting & tax services we provide. PDF.", href: SITE.files.accountingChecklist, testid: "download-accounting-checklist" },
  { title: "Chief Financial Officer Checklist", desc: "What our CFO advisory engagement looks like. PDF.", href: SITE.files.cfoChecklist, testid: "download-cfo-checklist" },
  { title: "Tax Services Intake Form", desc: `Review before your appointment. Please don't email the completed form; call ${SITE.phone} to arrange secure delivery. PDF.`, href: SITE.files.intakeForm, testid: "download-intake-form" },
  { title: "Tax Appointment Checklist", desc: "Documents to gather before your tax appointment.", href: SITE.files.taxAppointmentChecklist, testid: "download-tax-appt-checklist" },
];

function ResourceCard({ item }) {
  const isExternal = !!item.href;
  const testid = `resource-link-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`;
  const Inner = (
    <>
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded bg-gold-50 grid place-items-center text-gold-600">
          <item.icon size={20} />
        </div>
        <h3 className="font-serif-display text-[20px] text-navy-800 font-semibold leading-tight">{item.title}</h3>
      </div>
      <p className="mt-4 text-navy-600 text-[14.5px] leading-snug">{item.desc}</p>
      <div className="mt-5 inline-flex items-center gap-2 text-navy-700 text-sm font-medium group-hover:translate-x-0.5 transition-transform">
        {isExternal ? "Open at irs.gov" : "Open"} {isExternal ? <ExternalLink size={14} /> : <ArrowRight size={14} />}
      </div>
    </>
  );
  return isExternal ? (
    <a href={item.href} target="_blank" rel="noopener noreferrer" data-testid={testid} className="reveal card-lift block rounded border border-navy-100 bg-white p-7 group">{Inner}</a>
  ) : (
    <Link to={item.to} data-testid={testid} className="reveal card-lift block rounded border border-navy-100 bg-white p-7 group">{Inner}</Link>
  );
}

export default function Resources() {
  useReveal();
  return (
    <main data-testid="page-resources">
      <section className="pt-16 md:pt-20 pb-10 md:pb-14 border-b border-navy-100">
        <div className="container-narrow">
          <h1 className="font-serif-display text-4xl md:text-6xl text-navy-800 leading-[1.04] tracking-tight reveal">
            Tools & Resources
          </h1>
          <p className="mt-5 max-w-3xl text-navy-600 text-[17px] leading-relaxed reveal">
            The resource center is intended to make it as easy as possible for our clients to find the information that they need. Should you need any assistance, please don't hesitate to ask.
          </p>
        </div>
      </section>

      {/* Forms & Downloads — moved to top */}
      <section className="py-12 md:py-16 bg-cream-100/70 border-b border-navy-100" data-testid="resources-downloads">
        <div className="container-narrow">
          <div className="text-[12px] tracking-[0.24em] uppercase text-gold-500 font-medium reveal">Forms & Downloads</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-navy-800 mt-3 leading-[1.1] reveal">Get started with the right paperwork.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
            {DOWNLOADS.map((f) => (
              <a key={f.title} href={f.href} target="_blank" rel="noopener noreferrer" data-testid={f.testid} className="reveal card-lift block rounded bg-white border border-navy-100 p-6">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded bg-navy-50 grid place-items-center text-navy-700"><Download size={20} /></div>
                  <div className="font-serif-display text-[18px] text-navy-800 font-semibold">{f.title}</div>
                </div>
                <p className="mt-3 text-navy-600 text-[14.5px] leading-snug">{f.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Client Guides & References */}
      <section className="py-12 md:py-16">
        <div className="container-narrow">
          <div className="text-[12px] tracking-[0.24em] uppercase text-gold-500 font-medium reveal">Client Guides</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-navy-800 mt-3 leading-[1.1] reveal">Checklists, guides & references.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {INTERNAL_RESOURCES.map((r) => <ResourceCard key={r.to} item={r} />)}
          </div>
        </div>
      </section>

      {/* IRS Resources — moved to bottom */}
      <section className="py-12 md:py-16 bg-cream-100/70 border-t border-navy-100" data-testid="resources-irs">
        <div className="container-narrow">
          <div className="text-[12px] tracking-[0.24em] uppercase text-gold-500 font-medium reveal">IRS Resources</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-navy-800 mt-3 leading-[1.1] reveal">Straight from the source.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {EXTERNAL_RESOURCES.map((r) => <ResourceCard key={r.title} item={r} />)}
          </div>
        </div>
      </section>
    </main>
  );
}
